import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [activeOrg, setActiveOrg] = useState(null);
  const [loading, setLoading] = useState(true);

  const setActiveOrgSafely = useCallback((org) => {
    setActiveOrg(org);
    if (org?.id) localStorage.setItem("nexora_active_org_id", org.id);
    else localStorage.removeItem("nexora_active_org_id");
  }, []);

  const refresh = useCallback(async () => {
    // Cookie-first: /auth/me leest de HttpOnly `nexora_session`-cookie.
    // Vroeger gate'de we op localStorage["nexora_token"] → dat brak
    // Google SSO omdat de callback alleen de HttpOnly-cookie zet en
    // (server-side) geen bearer-token in localStorage kan schrijven.
    // Bearer-header blijft optioneel voor password-login (via api.js
    // interceptor) — /auth/me accepteert cookie én bearer.
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setOrgs(data.organizations || []);
      const savedId = localStorage.getItem("nexora_active_org_id");
      const chosen =
        (savedId && data.organizations.find((o) => o.id === savedId)) ||
        data.organizations[0] ||
        null;
      setActiveOrgSafely(chosen);
    } catch (e) {
      setUser(null); setOrgs([]); setActiveOrgSafely(null);
    } finally {
      setLoading(false);
    }
  }, [setActiveOrgSafely]);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("nexora_token", data.token);
    if (data.organization?.id) localStorage.setItem("nexora_active_org_id", data.organization.id);
    await refresh();
    return data;
  };

  const signup = async (payload) => {
    const { data } = await api.post("/auth/signup", payload);
    localStorage.setItem("nexora_token", data.token);
    if (data.organization?.id) localStorage.setItem("nexora_active_org_id", data.organization.id);
    await refresh();
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("nexora_token");
    localStorage.removeItem("nexora_active_org_id");
    setUser(null); setOrgs([]); setActiveOrg(null);
  };

  const switchOrg = async (orgId) => {
    const org = orgs.find((o) => o.id === orgId);
    if (org) setActiveOrgSafely(org);
  };

  return (
    <AuthCtx.Provider value={{ user, orgs, activeOrg, loading, login, signup, logout, switchOrg, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
